import { useState, useEffect } from 'react'
import {
  IDatLane,
  IRate,
  ICarrier,
  IRateAPI,
  RATE_ACTION_TYPE,
} from 'apps/marketplace/store/contractLanes/types'
import { ISpecificLane } from 'apps/marketplace/store/routingGuide/types'
import { initRate } from './initRate'
import { ChangeRateData } from './useCarrier'

type RateData = IRateAPI | { buttonClicked: boolean }

interface IUseRateDataProps {
  dialogOpen: boolean
  existingRate?: Partial<IRate>
  laneInfo?: IDatLane
  errors?: string | string[]
  carrier?: Partial<ICarrier>
  specificLane?: ISpecificLane
  pickupRadiusMiles?: number | null
  deliveryRadiusMiles?: number | null
}

export const useRateData: (
  arg: IUseRateDataProps
) => [IRateAPI, (changes: Partial<ChangeRateData>) => void] = ({
  carrier,
  deliveryRadiusMiles,
  dialogOpen,
  errors,
  existingRate,
  laneInfo,
  pickupRadiusMiles,
  specificLane,
}) => {
  const [rateData, setRateData] = useState<RateData>({
    buttonClicked: false,
  })

  const changeRateData = (changes: Partial<ChangeRateData>) => {
    setRateData((current: RateData) => ({ ...current, ...changes } as IRateAPI))
  }

  useEffect(() => {
    if (dialogOpen) {
      setRateData(initRate(laneInfo, existingRate, carrier))
    }
  }, [laneInfo, existingRate, dialogOpen, carrier])

  useEffect(() => {
    if (errors && errors.length && rateData?.buttonClicked) {
      changeRateData({ buttonClicked: false })
    }
  }, [errors])

  // If there is a specific lane passed in, use this to pre-populate for adding a new rate
  useEffect(() => {
    if (specificLane) {
      const [pCity = '', pState = ''] = specificLane.pickupCityState.split(',') || []
      const [dCity = '', dState = ''] = specificLane.deliveryCityState.split(',') || []
      changeRateData({
        pickupLocation: {
          city: pCity,
          state: pState.trim(),
        },
        deliveryLocation: {
          city: dCity,
          state: dState.trim(),
        },
        equipmentId: specificLane.equipment.id,
        isOnlyDropTrailer: specificLane.isDropTrailer,
        shipperId: specificLane.shipperApplication.id,
        shipperName: specificLane.shipperApplication.name,
        actionType: RATE_ACTION_TYPE.reserved,
        pickupRadiusMiles: pickupRadiusMiles === 1 ? 0 : pickupRadiusMiles || 0,
        deliveryRadiusMiles: deliveryRadiusMiles === 1 ? 0 : deliveryRadiusMiles || 0,
      })
    }
  }, [specificLane, pickupRadiusMiles, deliveryRadiusMiles])

  return [rateData as IRateAPI, changeRateData]
}
